import React from 'react';
import styles from './StepNavigationTabs.module.css';

const StepNavigationTabs = ({
  listingMode,
  formStep,
  setFormStep,
  isAsinDataFetched,
  isFormStepValid,
  // Optional: when provided, tab clicks route through this handler so an invalid
  // forward jump surfaces validation highlights (matching the Next button) instead
  // of being a no-op. Falls back to direct setFormStep navigation when absent.
  onTabSelect,
  isThemesPanelOpen,
  isFontsPanelOpen,
  showVariantsTab,
  getTotalFormSteps: getTotalFormStepsProp, // optional override (e.g. bulk Product Copy: 3 or 4 steps)
  hideAsinLookupTab = false, // when true (e.g. bulk existing), step 1 (ASIN Lookup) is hidden from tabs
}) => {
  // Get total form steps: use prop if provided (bulk), else default by listing mode
  const getTotalFormSteps = getTotalFormStepsProp || (() => {
    const baseSteps = listingMode === 'new' ? 4 : 5;
    return showVariantsTab ? baseSteps + 1 : baseSteps;
  });

  // Get step label for tabs
  const getStepLabel = (step) => {
    if (listingMode === 'new') {
      switch (step) {
        case 1: return 'Product Identity';
        case 2: return 'Details';
        case 3: return 'Description';
        case 4: return showVariantsTab ? 'Variants' : 'Infographics';
        case 5: return showVariantsTab ? 'Infographics' : '';
        default: return '';
      }
    } else {
      switch (step) {
        case 1: return 'Asin Lookup';
        case 2: return 'Existing Listing';
        case 3: return 'Details';
        case 4: return 'Description';
        case 5: return showVariantsTab ? 'Variants' : 'Infographics';
        case 6: return showVariantsTab ? 'Infographics' : '';
        default: return '';
      }
    }
  };

  const totalSteps = getTotalFormSteps();
  const stepsToShow = (hideAsinLookupTab && listingMode === 'existing')
    ? Array.from({ length: totalSteps }, (_, i) => i + 1).filter((s) => s !== 1)
    : Array.from({ length: totalSteps }, (_, i) => i + 1);

  return (
    <div className={styles.navSection}>
      <div role="tablist" className={`${styles.navTabs} ${(isThemesPanelOpen || isFontsPanelOpen) ? styles.navTabsWithPanel : ''}`}>
        {stepsToShow.map((step) => {
          const isActive = formStep === step;
          const preAsinLock = listingMode === 'existing' && !isAsinDataFetched;

          // When onTabSelect is provided, forward jumps are handled there (validate +
          // highlight), so every tab is interactive except while the existing-mode ASIN
          // lookup is still pending. Otherwise fall back to the legacy validity gate.
          let isClickable;
          if (onTabSelect) {
            isClickable = preAsinLock ? step === 1 : true;
          } else if (preAsinLock) {
            isClickable = step === 1;
          } else if (step <= formStep) {
            // Can always navigate to current or previous steps
            isClickable = true;
          } else {
            // For future steps, check if all previous steps are valid
            // This allows jumping ahead if all required steps are completed
            let allPreviousValid = true;
            for (let prevStep = 1; prevStep < step; prevStep++) {
              if (isFormStepValid && !isFormStepValid(prevStep)) {
                allPreviousValid = false;
                break;
              }
            }
            isClickable = allPreviousValid;
          }

          const activate = () => {
            if (!isClickable) return;
            if (onTabSelect) onTabSelect(step);
            else setFormStep(step);
          };

          return (
            <div
              key={step}
              role="tab"
              aria-selected={isActive}
              tabIndex={isClickable ? 0 : -1}
              className={isActive ? styles.navTab : styles.navTabInactive}
              onClick={activate}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  activate();
                }
              }}
              style={{ cursor: isClickable ? 'pointer' : 'default' }}
            >
              {isActive ? (
                <>
                  <div className={styles.navTabLabel}>{getStepLabel(step)}</div>
                  <div key={formStep} className={styles.navTabIndicator}></div>
                </>
              ) : (
                <div>{getStepLabel(step)}</div>
              )}
            </div>
          );
        })}
      </div>
      <div className={styles.navTabsSeparator}></div>
    </div>
  );
};

export default StepNavigationTabs;

